import React, { ComponentType } from 'react';

import { URL } from '@Pages/url';
import { KnowledgeList } from '@Pages/Knowledge/setting';
import s from './styles/KnowledgeDetail.module.css';

// index.tsx（CSR、lazy()+Suspense）とindex.ssr.tsx（SSR専用、eager import）の両方が
// このBodyを共有する。lazy()の戻り値もeager importの戻り値もReact.ComponentTypeとして
// 同じ形でJSXに渡せるため、「どちらの読み込み方をしたか」だけが差分になり、
// 実際の画面構造（switch分岐相当のarticles参照・JSXツリー）は1箇所にしか存在しない。
export type KnowledgeDetailComponents = {
    Breadcrumb: ComponentType<any>;
    FooterInquiryPC: ComponentType<any>;
    FooterInquirySP: ComponentType<any>;
    PagePC: ComponentType<any>;
    PageSP: ComponentType<any>;
    Heading1: ComponentType<any>;
    KnowledgePagger: ComponentType<any>;
    // 記事本文コンポーネント(id => Component)。switch文の代わりにルックアップで解決する。
    articles: Record<number, ComponentType<any>>;
};

type KnowledgeDetailProps = {
    id: number;
    isSp: boolean;
    h1Override?: string | null;
};

// currentPathはRailsのProps（業務データ）に含めず、packs/application.js・server-bundle.jsの
// register関数がrailsContext.pathnameから渡すpropとして別枠で受け取る（PR #2082 Point2対応）
type KnowledgeDetailPropsWithPath = KnowledgeDetailProps & {
    currentPath?: string;
    components: KnowledgeDetailComponents;
};

export const KnowledgeDetailBody = (props: KnowledgeDetailPropsWithPath) => {
    const { components } = props;
    const Page = props.isSp ? components.PageSP : components.PagePC;
    const FooterInquiry = props.isSp ? components.FooterInquirySP : components.FooterInquiryPC;
    const knowledge = KnowledgeList.find((v) => v.id === props.id);
    // id+1/id-1の単純な前後探索だと、並行実装中の別Phaseが未マージの間にid欠番
    // （例: 46〜49が別PR未マージの間の45↔50)ができ、前後リンクが欠落する。
    // ソート済み配列上の隣接要素を見ることで欠番があっても前後記事へリンクできるようにする。
    const sortedKnowledgeList = [...KnowledgeList].sort((a, b) => a.id - b.id);
    const currentIndex = sortedKnowledgeList.findIndex((v) => v.id === props.id);
    const prevKnowledge = currentIndex > 0 ? sortedKnowledgeList[currentIndex - 1] : undefined;
    const nextKnowledge =
        currentIndex >= 0 && currentIndex < sortedKnowledgeList.length - 1
            ? sortedKnowledgeList[currentIndex + 1]
            : undefined;

    const breadcrumbs = [
        {
            name: 'トップ',
            url: '/',
        },
        {
            name: '会員権の基礎知識',
            url: URL.KNOWLEDGE,
        },
        {
            name: knowledge.title,
            url: '',
        },
    ];

    return (
        <Page
            contentHead={
                <>
                    <components.Breadcrumb breadcrumbList={breadcrumbs} withoutBackground={true} />
                    <HeadContent
                        id={props.id}
                        isSp={props.isSp}
                        h1Override={props.h1Override}
                        Heading1={components.Heading1}
                    />
                </>
            }
            contentFooter={
                <div className={s.inquiry_area}>
                    <FooterInquiry />
                </div>
            }
            currentPath={props.currentPath}
        >
            <DetailContent id={props.id} articles={components.articles} />
            <components.KnowledgePagger prevKnowledge={prevKnowledge} nextKnowledge={nextKnowledge} />
        </Page>
    );
};

type HeadContentProps = KnowledgeDetailProps & { Heading1: ComponentType<any> };

const HeadContent = (props: HeadContentProps) => {
    const knowledge = KnowledgeList.find((v) => v.id == props.id);
    const h1 = props.h1Override || knowledge.title;
    return (
        <div className={s.head_content}>
            <div className={s.head_content_inner}>
                <props.Heading1 title={h1} />
                <div>{knowledge.description}</div>
            </div>
        </div>
    );
};

type DetailContentProps = { id: number; articles: Record<number, ComponentType<any>> };

const DetailContent = (props: DetailContentProps) => {
    const Article = props.articles[props.id];
    return Article ? <Article /> : null;
};
